import { useTranslations } from '@aikoa/i18n/react'
import { View } from 'react-native'
import { Detail } from '~/components/typography/Content'
import { cn } from '~/lib/utils'

export const AIKOA_FEE_PERCENT = 4
export const LEAD_OWNER_FEE_PERCENT = 18
export const LEAD_BUYER_FEE_PERCENT = 78

type CommissionSplitBarVariant = 'default' | 'inverted'

type CommissionSegment = {
  percent: number
  label: string
  sublabel?: string
  colorClass: string
  legendClass: string
  textColorClass: string
  legendTextColorClass: string
}

export function CommissionSplitBar({
  variant = 'default',
  className,
}: {
  variant?: CommissionSplitBarVariant
  className?: string
}) {
  const { t } = useTranslations('leads')

  const commissionSegments: CommissionSegment[] = [
    {
      percent: LEAD_BUYER_FEE_PERCENT + 1,
      label: t(($) => $.requestContact.you),
      sublabel: `${LEAD_BUYER_FEE_PERCENT}%`,
      colorClass: variant === 'inverted' ? 'bg-background' : 'bg-primary',
      legendClass: variant === 'inverted' ? 'bg-background' : 'bg-primary',
      textColorClass: variant === 'inverted' ? 'text-primary' : 'text-background',
      legendTextColorClass: variant === 'inverted' ? 'text-background' : 'text-primary',
    },
    {
      percent: AIKOA_FEE_PERCENT - 2,
      label: t(($) => $.requestContact.aikoa),
      sublabel: `${AIKOA_FEE_PERCENT}%`,
      colorClass: 'bg-accent',
      legendClass: 'bg-accent',
      textColorClass: 'text-background',
      legendTextColorClass: variant === 'inverted' ? 'text-background' : 'text-primary',
    },
    {
      percent: LEAD_OWNER_FEE_PERCENT + 1,
      label: t(($) => $.requestContact.leadOwner),
      sublabel: `${LEAD_OWNER_FEE_PERCENT}%`,
      colorClass: variant === 'inverted' ? 'bg-secondary' : 'bg-secondary',
      legendClass: variant === 'inverted' ? 'bg-secondary' : 'bg-secondary',
      textColorClass: 'text-background',
      legendTextColorClass: variant === 'inverted' ? 'text-background' : 'text-primary',
    },
  ]

  return (
    <View className={cn('gap-6', className)}>
      <View className="w-full flex-1 flex-row pt-4">
        <View className="h-14 flex-1 flex-row rounded-xl">
          {commissionSegments.map((segment, index) => (
            <View
              key={segment.label}
              style={{ flexBasis: `${segment.percent}%`, width: `${segment.percent}%` }}
              className={cn(
                'relative items-center justify-center',
                segment.colorClass,
                index === 0 && 'rounded-l-xl',
                index === commissionSegments.length - 1 && 'rounded-r-xl'
              )}>
              <Detail
                className={cn(
                  'font-semibold text-xs',
                  segment.textColorClass,
                  segment.sublabel === `${AIKOA_FEE_PERCENT}%` && 'absolute -top-6 text-accent'
                )}>
                {segment.sublabel}
              </Detail>
            </View>
          ))}
        </View>
      </View>

      <View className="flex-row justify-between">
        {commissionSegments.map((segment) => (
          <View key={segment.label} className="flex-1 items-center gap-1">
            <View className={cn('size-4 rounded-full', segment.legendClass)} />
            <Detail className={cn('text-center text-xs', segment.legendTextColorClass)}>
              {segment.label}
            </Detail>
          </View>
        ))}
      </View>
    </View>
  )
}
